import { useState } from "react";
import ColorInput from "./ColorInput";
import GradientEditor from "./GradientEditor";
import NumberInput from "./NumberInput";
import { WEIGHT_OPTIONS, FONT_OPTIONS } from "../../utils/fonts";
import { toast } from "react-toastify";
import LiveSelect from "./LiveSelect";

import {
  Image as ImageIcon,
  AlignStartVertical as AlignLeftIcon,
  AlignCenterVertical as AlignCenterIcon,
  AlignEndVertical as AlignRightIcon,
  AlignStartHorizontal as AlignTopIcon,
  AlignCenterHorizontal as AlignMiddleIcon,
  AlignEndHorizontal as AlignBottomIcon,
  Lock,
} from "lucide-react";

const PX_PER = { px: 1, in: 96, cm: 37.7953, mm: 3.77953 };
const UNIT_DEC = { px: 0, in: 2, cm: 2, mm: 1 };
const toUnit = (px, u) => +(px / PX_PER[u]).toFixed(UNIT_DEC[u]);
const toPx = (v, u) => Math.round(v * PX_PER[u]);

function UnitSwitcher({ unit, setUnit }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
        Unit
      </span>
      <div className="flex bg-gray-100/80 rounded-lg p-0.5">
        {["px", "in", "cm", "mm"].map((u) => (
          <button
            key={u}
            type="button"
            onClick={() => setUnit(u)}
            className={`px-1.5 py-0.5 text-[10px] font-bold rounded-md transition-all ${
              unit === u
                ? "bg-white text-indigo-600 shadow-sm"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            {u}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SectionControls({
  id,
  canvas,
  def,
  measuredSize,
  onChange,
  hideHeadingControls = false,
}) {
  const [unit, setUnit] = useState("px");

  if (!def) return null;

  const set = (patch) => onChange({ ...def, ...patch });
  const hasPositiveNumber = (value) =>
    Number.isFinite(Number(value)) && Number(value) > 0;
  const isQrLayer = def.imageKey === "qrData" || id === "qr";
  const displayWidth = hasPositiveNumber(def.width)
    ? def.width
    : measuredSize?.width || 0;
  const displayHeight = hasPositiveNumber(def.height)
    ? def.height
    : measuredSize?.height || 0;
  const fallbackSectionRadius = Number(def.borderRadius ?? 8) || 0;
  const sectionCornerRadii = {
    all: fallbackSectionRadius,
    topLeft: Number(def.borderTopLeftRadius ?? fallbackSectionRadius) || 0,
    topRight: Number(def.borderTopRightRadius ?? fallbackSectionRadius) || 0,
    bottomRight: Number(def.borderBottomRightRadius ?? fallbackSectionRadius) || 0,
    bottomLeft: Number(def.borderBottomLeftRadius ?? fallbackSectionRadius) || 0,
  };
  const sectionFillMode =
    def.fillMode ||
    (def.gradientStops || def.backgroundGradientFrom || def.backgroundGradientTo
      ? "gradient"
      : "solid");
  const handleAlignHorizontal = (align) => {
    const canvasWidth = canvas?.width || 400;
    let nextWidth = def.width;
    let nextX = def.x || 0;

    // For text-based layers, if no width exists and they want to center,
    // it's usually best to make them full width or use a default.
    if ((def.type === "text" || def.type === "pair") && !def.width) {
      if (align === "center" || align === "right") {
        nextWidth = canvasWidth;
        nextX = 0;
      }
    } else {
      const layerWidth = def.width || 0;
      if (align === "left") nextX = 0;
      else if (align === "center") nextX = (canvasWidth - layerWidth) / 2;
      else if (align === "right") nextX = canvasWidth - layerWidth;
    }

    const patch = { x: Math.round(nextX) };
    if (nextWidth) patch.width = nextWidth;

    // Also update inner alignment for text/pair/pill to match
    if (["text", "pair", "pill"].includes(def.type)) {
      patch.align = align;
    }

    set(patch);
  };

  const handleAlignVertical = (vAlign) => {
    const canvasHeight = canvas?.height || 560;
    const layerHeight = def.height || (def.fontSize ? def.fontSize * 1.5 : 40);

    let nextY = def.y || 0;
    if (vAlign === "top") nextY = 0;
    else if (vAlign === "middle") nextY = (canvasHeight - layerHeight) / 2;
    else if (vAlign === "bottom") nextY = canvasHeight - layerHeight;

    set({ y: Math.round(nextY), verticalAlign: vAlign });
  };

  const setCornerRadius = (patch) => {
    const nextPatch = { ...patch };
    if ("borderRadius" in nextPatch) {
      nextPatch.borderTopLeftRadius = nextPatch.borderRadius;
      nextPatch.borderTopRightRadius = nextPatch.borderRadius;
      nextPatch.borderBottomRightRadius = nextPatch.borderRadius;
      nextPatch.borderBottomLeftRadius = nextPatch.borderRadius;
    }
    set(nextPatch);
  };

  const renderCornerRadiusControls = (max = 200) => (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
            Corner Radius
          </div>
        </div>
        <div
          className="h-14 w-14 rounded-2xl border border-gray-200 bg-gray-50"
          style={{
            borderTopLeftRadius: sectionCornerRadii.topLeft,
            borderTopRightRadius: sectionCornerRadii.topRight,
            borderBottomRightRadius: sectionCornerRadii.bottomRight,
            borderBottomLeftRadius: sectionCornerRadii.bottomLeft,
          }}
        ></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <NumberInput
          label="All Corners (px)"
          value={sectionCornerRadii.all}
          min={0}
          max={max}
          onChange={(v) => setCornerRadius({ borderRadius: v })}
        />
        <div></div>
        <NumberInput
          label="Top Left"
          value={sectionCornerRadii.topLeft}
          min={0}
          max={max}
          onChange={(v) => setCornerRadius({ borderTopLeftRadius: v })}
        />
        <NumberInput
          label="Top Right"
          value={sectionCornerRadii.topRight}
          min={0}
          max={max}
          onChange={(v) => setCornerRadius({ borderTopRightRadius: v })}
        />
        <NumberInput
          label="Bottom Left"
          value={sectionCornerRadii.bottomLeft}
          min={0}
          max={max}
          onChange={(v) => setCornerRadius({ borderBottomLeftRadius: v })}
        />
        <NumberInput
          label="Bottom Right"
          value={sectionCornerRadii.bottomRight}
          min={0}
          max={max}
          onChange={(v) => setCornerRadius({ borderBottomRightRadius: v })}
        />
      </div>
    </div>
  );

  const renderTextAlignControls = () => (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-[0.16em]">
        Text Align
      </span>
      <div className="grid grid-cols-3 gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1">
        {[
          { value: "left", label: "Left" },
          { value: "center", label: "Center" },
          { value: "right", label: "Right" },
        ].map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => set({ align: option.value })}
            className={`rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
              (def.align || "center") === option.value
                ? "bg-white text-indigo-600 shadow-sm"
                : "text-gray-500 hover:bg-white hover:text-gray-700"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderTypeSpecific = () => {
    switch (def.type) {
      case "text":
        return (
          <div className="controls-group">
            <div className="row">
              <div className="ctrl !flex-col !items-start">
                <LiveSelect
                  label="Font"
                  value={def.fontFamily}
                  options={FONT_OPTIONS.map((f) => ({ value: f, label: f }))}
                  isFont={true}
                  onChange={(v) => set({ fontFamily: v })}
                />
              </div>
              <LiveSelect
                label="Weight"
                value={def.fontWeight}
                options={WEIGHT_OPTIONS}
                onChange={(v) => set({ fontWeight: Number(v) })}
              />
              <NumberInput
                label="Size"
                value={def.fontSize}
                min={8}
                max={48}
                onChange={(v) => set({ fontSize: v })}
              />
            </div>
            <div className="row">
              <NumberInput
                label="Line spacing"
                value={def.lineSpacing ?? 0}
                min={0}
                max={80}
                onChange={(v) => set({ lineSpacing: v })}
              />
              <NumberInput
                label="Word spacing"
                value={def.wordSpacing ?? 0}
                min={0}
                max={80}
                onChange={(v) => set({ wordSpacing: v })}
              />
              <NumberInput
                label="Letter spacing"
                value={def.letterSpacing ?? 0}
                min={0}
                max={80}
                step={0.1}
                onChange={(v) => set({ letterSpacing: v })}
              />
            </div>
            <ColorInput
              label="Color"
              value={def.color}
              onChange={(v) => set({ color: v })}
            />
            {renderTextAlignControls()}
            {def.text !== undefined && id !== "venue" && (
              <label className="ctrl">
                <span>Static text</span>
                <textarea
                  className="ctrl-text"
                  rows={2}
                  value={def.text}
                  onChange={(e) => set({ text: e.target.value })}
                />
              </label>
            )}
          </div>
        );

      case "pair":
        return (
          <div className="controls-group">
            <NumberInput
              label="Letter spacing"
              value={def.letterSpacing ?? 0}
              min={0}
              max={80}
              step={0.1}
              onChange={(v) => set({ letterSpacing: v })}
            />
            {!hideHeadingControls &&
              (() => {
                // The displayed heading is: custom override → fallback to section label
                const hasVisibleLabel = !!(def.heading || def.label);
                return (
                  <>
                    {/* Label font size only makes sense when a label is actually shown */}
                    {hasVisibleLabel && (
                      <NumberInput
                        label="Label font size"
                        value={def.headingFontSize}
                        min={8}
                        max={24}
                        onChange={(v) => set({ headingFontSize: v })}
                      />
                    )}
                    <NumberInput
                      label="Value font size"
                      value={def.valueFontSize}
                      min={8}
                      max={32}
                      onChange={(v) => set({ valueFontSize: v })}
                    />
                    <label className="ctrl">
                      <span>Label text</span>
                      <textarea
                        className="ctrl-text"
                        rows={1}
                        placeholder={def.label || "e.g. Public Id"}
                        value={def.heading ?? ""}
                        onChange={(e) => set({ heading: e.target.value })}
                      />
                    </label>
                  </>
                );
              })()}
            <ColorInput
              label="Color"
              value={def.color}
              onChange={(v) => set({ color: v })}
            />
            <NumberInput
              label="Spacing below"
              value={def.spacing}
              min={0}
              max={48}
              onChange={(v) => set({ spacing: v })}
            />
          </div>
        );

      case "pill":
        return (
          <div className="controls-group">
            <NumberInput
              label="Letter spacing"
              value={def.letterSpacing ?? 0}
              min={0}
              max={80}
              step={0.1}
              onChange={(v) => set({ letterSpacing: v })}
            />
            <GradientEditor
              mode={sectionFillMode}
              onModeChange={(v) => set({ fillMode: v })}
              angle={Number(def.gradientAngle ?? 135)}
              onAngleChange={(v) => set({ gradientAngle: v })}
              stops={def.gradientStops || [
                {
                  id: `${id}-pill-stop-1`,
                  position: 0,
                  color: def.backgroundGradientFrom || def.bg || "#4f46e5",
                  opacity: 100,
                },
                {
                  id: `${id}-pill-stop-2`,
                  position: 100,
                  color: def.backgroundGradientTo || "#a855f7",
                  opacity: 100,
                },
              ]}
              onStopsChange={(stops) =>
                set({
                  gradientStops: stops,
                  backgroundGradientFrom: stops[0]?.color || def.bg || "#4f46e5",
                  backgroundGradientTo:
                    stops[stops.length - 1]?.color || "#a855f7",
                })
              }
              fallback={[def.bg || "#4f46e5", "#a855f7"]}
            />
            {sectionFillMode !== "gradient" && (
              <ColorInput
                label="Background"
                value={def.bg}
                onChange={(v) => set({ bg: v })}
                canBeTransparent={true}
              />
            )}
            <ColorInput
              label="Text color"
              value={def.textColor}
              onChange={(v) => set({ textColor: v })}
            />
            <NumberInput
              label="Font size"
              value={def.fontSize}
              min={8}
              max={32}
              onChange={(v) => set({ fontSize: v })}
            />
            <NumberInput
              label="Height"
              value={def.height}
              min={18}
              max={48}
              onChange={(v) => set({ height: v })}
            />
            <NumberInput
              label="Spacing below"
              value={def.spacing}
              min={0}
              max={48}
              onChange={(v) => set({ spacing: v })}
            />
            {renderCornerRadiusControls(100)}
          </div>
        );

      case "imagesRow":
        return (
          <div className="controls-group">
            <NumberInput
              label="Image size"
              value={def.size}
              min={60}
              max={180}
              onChange={(v) => set({ size: v })}
            />
            <NumberInput
              label="Gap"
              value={def.gap}
              min={0}
              max={80}
              onChange={(v) => set({ gap: v })}
            />
            <NumberInput
              label="Spacing below"
              value={def.spacingBelow}
              min={0}
              max={48}
              onChange={(v) => set({ spacingBelow: v })}
            />
          </div>
        );

      case "divider":
        return (
          <div className="controls-group">
            <ColorInput
              label="Color"
              value={def.color}
              onChange={(v) => set({ color: v })}
            />
            <NumberInput
              label="Thickness"
              value={def.thickness}
              min={1}
              max={6}
              onChange={(v) => set({ thickness: v })}
            />
            <NumberInput
              label="Spacing below"
              value={def.spacing}
              min={0}
              max={48}
              onChange={(v) => set({ spacing: v })}
            />
          </div>
        );

      case "image":
        return (
          <div className="controls-group">
            {!def.imageKey && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-center w-full mt-2">
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <ImageIcon size={20} className="text-gray-400 mb-2" />
                      <p className="mb-1 text-xs text-gray-500">
                        <span className="font-semibold">Click to upload</span>
                      </p>
                      <span className="text-[10px] text-gray-400">
                        PNG, JPG, GIF or WEBP
                      </span>
                    </div>
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,.gif,.jfif,image/png,image/jpeg,image/webp,image/gif,image/jfif"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const ext = String(file.name || "").split(".").pop().toLowerCase();
                          const ALLOWED_EXTENSIONS = new Set(["png", "jpg", "jpeg", "webp", "gif", "jfif"]);
                          if (!ALLOWED_EXTENSIONS.has(ext) || ["tiff", "tif"].includes(ext) || file.type === "image/tiff") {
                            toast.error("Please upload correct image format");
                            e.target.value = "";
                            return;
                          }
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error("Image size should be less than 10MB");
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = (event) =>
                            set({ customImage: event.target.result });
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </label>
                </div>

                {def.customImage && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-600">
                        Preview
                      </span>
                      <button
                        onClick={() => set({ customImage: "" })}
                        className="text-[10px] text-red-500 hover:text-red-700 bg-red-50 px-2 py-0.5 rounded cursor-pointer"
                      >
                        Remove
                      </button>
                    </div>
                    <img
                      src={def.customImage}
                      alt="Custom"
                      className="w-full h-auto max-h-24 object-contain rounded border border-gray-200 bg-white"
                    />
                  </div>
                )}
              </div>
            )}
            {!isQrLayer && (
              <div className="mt-4">
                {renderCornerRadiusControls(200)}
              </div>
            )}
            {isQrLayer && (
              <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  QR Border
                </div>
                <ColorInput
                  label="Border Color"
                  value={def.qrBorderColor || "#000000"}
                  onChange={(v) => set({ qrBorderColor: v })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <NumberInput
                    label="Border Width (px)"
                    value={def.qrBorderWidth || 0}
                    min={0}
                    max={20}
                    onChange={(v) => set({ qrBorderWidth: v })}
                  />
                  <NumberInput
                    label="Gap (px)"
                    value={def.qrBorderGap || 0}
                    min={0}
                    max={60}
                    onChange={(v) => set({ qrBorderGap: v })}
                  />
                </div>
              </div>
            )}
          </div>
        );

      case "shape":
        return (
          <div className="controls-group">
            <LiveSelect
              label="Type"
              value={def.shapeType || "rectangle"}
              options={[
                { value: "rectangle", label: "Rectangle" },
                { value: "circle", label: "Circle" },
              ]}
              onChange={(v) => set({ shapeType: v })}
            />
            <GradientEditor
              mode={sectionFillMode}
              onModeChange={(v) => set({ fillMode: v })}
              angle={Number(def.gradientAngle ?? 135)}
              onAngleChange={(v) => set({ gradientAngle: v })}
              stops={def.gradientStops || [
                {
                  id: `${id}-shape-stop-1`,
                  position: 0,
                  color:
                    def.backgroundGradientFrom || def.backgroundColor || "#cccccc",
                  opacity: 100,
                },
                {
                  id: `${id}-shape-stop-2`,
                  position: 100,
                  color: def.backgroundGradientTo || "#a855f7",
                  opacity: 100,
                },
              ]}
              onStopsChange={(stops) =>
                set({
                  gradientStops: stops,
                  backgroundGradientFrom:
                    stops[0]?.color || def.backgroundColor || "#cccccc",
                  backgroundGradientTo:
                    stops[stops.length - 1]?.color || "#a855f7",
                })
              }
              fallback={[def.backgroundColor || "#cccccc", "#a855f7"]}
            />
            {sectionFillMode !== "gradient" && (
              <ColorInput
                label="Background Color"
                value={def.backgroundColor}
                onChange={(v) => set({ backgroundColor: v })}
                canBeTransparent={true}
              />
            )}
            <ColorInput
              label="Border Color"
              value={def.borderColor}
              onChange={(v) => set({ borderColor: v })}
            />
            <div className="grid grid-cols-2 gap-3 mt-4">
              <NumberInput
                label="Border Width (px)"
                value={def.borderWidth || 0}
                min={0}
                max={20}
                onChange={(v) => set({ borderWidth: v })}
              />
              <NumberInput
                label="Border Radius (px)"
                value={sectionCornerRadii.all}
                min={0}
                max={200}
                onChange={(v) => setCornerRadius({ borderRadius: v })}
              />
            </div>
            {def.shapeType !== "circle" && renderCornerRadiusControls(200)}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {renderTypeSpecific()}

      {/* Standard Alignment & Position - Applied to EVERY layer */}
      <div className="mt-4 pt-5 border-t border-gray-100 flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Alignment
          </span>
          <div className="flex items-center gap-4">
            {/* Horizontal Alignment */}
            <div className="flex bg-gray-100/50 rounded-lg p-1 gap-1">
              {[
                { val: "left", icon: AlignLeftIcon, label: "Left" },
                { val: "center", icon: AlignCenterIcon, label: "Center" },
                { val: "right", icon: AlignRightIcon, label: "Right" },
              ].map((opt) => (
                <button
                  key={opt.val}
                  title={opt.label}
                  onClick={() => handleAlignHorizontal(opt.val)}
                  className={`p-1.5 rounded-md transition-all ${
                    def.align === opt.val
                      ? "bg-white shadow-sm text-indigo-600 outline outline-1 outline-indigo-100"
                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <opt.icon size={18} />
                </button>
              ))}
            </div>

            {/* Vertical Alignment */}
            <div className="flex bg-gray-100/50 rounded-lg p-1 gap-1">
              {[
                { val: "top", icon: AlignTopIcon, label: "Top" },
                { val: "middle", icon: AlignMiddleIcon, label: "Middle" },
                { val: "bottom", icon: AlignBottomIcon, label: "Bottom" },
              ].map((opt) => (
                <button
                  key={opt.val}
                  title={opt.label}
                  onClick={() => handleAlignVertical(opt.val)}
                  className={`p-1.5 rounded-md transition-all ${
                    def.verticalAlign === opt.val ||
                    (!def.verticalAlign && opt.val === "middle")
                      ? "bg-white shadow-sm text-indigo-600 outline outline-1 outline-indigo-100"
                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <opt.icon size={18} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between pb-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Position & Size
            </span>
            <UnitSwitcher unit={unit} setUnit={setUnit} />
          </div>

          <div className="grid grid-cols-2 gap-x-5 gap-y-5">
            <NumberInput
              label={`X position (${unit})`}
              step={unit === "px" ? 1 : 0.1}
              value={toUnit(def.x || 0, unit)}
              onChange={(v) => set({ x: toPx(v, unit) })}
            />
            <NumberInput
              label={`Y position (${unit})`}
              step={unit === "px" ? 1 : 0.1}
              value={toUnit(def.y || 0, unit)}
              onChange={(v) => set({ y: toPx(v, unit) })}
            />
            <div className="relative col-span-2 grid grid-cols-2 gap-x-5 gap-y-5">
              <NumberInput
                label={`Width (${unit})`}
                step={unit === "px" ? 1 : 0.1}
                min={toUnit(isQrLayer ? 40 : 10, unit)}
                value={toUnit(displayWidth, unit)}
                onChange={(v) => {
                  const px = toPx(v, unit);
                  if (def.keepSquare) {
                    set({ width: px, height: px });
                  } else {
                    set({ width: px });
                  }
                }}
              />
              <NumberInput
                label={`Height (${unit})`}
                step={unit === "px" ? 1 : 0.1}
                min={toUnit(isQrLayer ? 40 : 10, unit)}
                value={toUnit(displayHeight, unit)}
                onChange={(v) => {
                  const px = toPx(v, unit);
                  if (def.keepSquare) {
                    set({ width: px, height: px });
                  } else {
                    set({ height: px });
                  }
                }}
              />
              {def.keepSquare && (
                <div
                  className="absolute left-1/2 top-[60%] -translate-x-1/2 -translate-y-1/2 flex items-center justify-center text-indigo-400"
                  title="Aspect ratio locked (Square)"
                >
                  <Lock size={15} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
